// client/src/components/pages/LexiStar/StatusBoard/columns.tsx (converted from .jsx, ST-00206)
//
// Status-board columns (FR-STAT-001, LS-09 §5.3 worked row):
// JX12KLGP | T. Nkosi | In transit · moving | N4 near eMalahleni | 64 km/h (80) |
// Mine A → Siding B | 131/212 km 62% · avg 214 | ETA 11:42 | Budget 78 L |
// Used 53 L ● 68% | Burn 1.10 ▲ | −7 L | 2h14 in status | 12 s ago
//
// Each column: id, label, render(row, ctx), sortValue(row), defaultVisible,
// and `manager` for the rand column (LS-09: rand only in a manager's set).
//
// ST-00206: the Stage cell shows "reminded ×N" (AckRemindersTag) next to a DISPATCHED
// trip the driver has not accepted after LexiStar reminded them; sorting and the CSV
// export keep the stage alone. ST-00205: and "waiting for first report" (AwaitingReportTag)
// while the truck has not reported since the dispatch.

import type { ReactNode } from 'react';
import {
  StatusPill,
  StagePill,
  ProvenanceMarker,
  BurnIndex,
  OverConsumptionTag,
  RunChip,
  DataAge,
  AckRemindersTag,
  AwaitingReportTag,
  type RunSummary,
} from './atoms';
import { isAboveRouteNorm } from './fuelLogic';
import { combinedStatusLabel, STAGE_LABELS as STAGE_LABELS_JS } from './statusLanguage';
import { statusKeyOfRow } from './controlRoom';
import {
  fmtNum,
  fmtPct,
  fmtL,
  fmtSignedL,
  fmtRand,
  fmtTime,
  fmtDuration,
  secondsSince,
} from './format';
import type {
  StatusBoardFuel,
  StatusBoardRow,
  StatusBoardTrip,
} from '../../../../types/lexistar-status-board';

const STAGE_LABELS = STAGE_LABELS_JS as Record<string, string>;

/** What a cell can use besides the row: the board's clock and the run drawer. */
export interface ColumnCtx {
  now?: number;
  onOpenRun?: (run: RunSummary) => void;
}

export interface StatusBoardColumn {
  id: string;
  label: string;
  /** Always shown (not in the column chooser). */
  locked?: boolean;
  /** Only in a manager's column set (LS-09: rand values). */
  manager?: boolean;
  defaultVisible?: boolean;
  sortValue: (row: StatusBoardRow, ctx: ColumnCtx) => string | number;
  render: (row: StatusBoardRow, ctx: ColumnCtx) => ReactNode;
  csv: (row: StatusBoardRow, ctx: ColumnCtx) => string | number;
}

const stageLabel = (stage: string | null | undefined): string =>
  stage ? STAGE_LABELS[stage] || '' : '';

const dash = <span className="text-xs text-slate-400 italic">—</span>;

export const statusSince = (row: StatusBoardRow): string | null =>
  row.status?.since || row.trip?.stage_since || null;

/** Registration, or the engine's key for a truck not in the register yet. */
export const rowRegistration = (row: {
  registration?: string | null;
  asset_key?: string | null;
}): string => row.registration || String(row.asset_key || '').replace(/^(reg|asset):/, '') || '—';

/** No driver register in M2: the name when known, else the driver tag. */
export const rowDriver = (row: {
  driver_name?: string | null;
  driver_tag?: string | null;
}): string | null => row.driver_name || row.driver_tag || null;

/** Data age: the device time of the latest position, else when the engine last saw it. */
export const rowUpdatedAt = (row: StatusBoardRow): string | null =>
  row.position?.ts || row.last_update || null;

export function DistanceCell({ trip }: { trip: StatusBoardTrip | null | undefined }) {
  if (!trip) return dash;
  const planned = trip.adjusted_km ?? trip.planned_km;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm tabular-nums whitespace-nowrap">
      <span>
        {fmtNum(trip.km_travelled)}/{fmtNum(planned)} km
      </span>
      <ProvenanceMarker provenance={trip.distance_provenance} />
      <span className="text-slate-500">{fmtPct(trip.pct_distance)}</span>
      <span
        className="text-xs text-slate-500"
        title="Route average distance over the last 90 days — a sense check, not a target. Shown once five qualifying trips exist."
      >
        · avg {trip.route_avg_km != null ? fmtNum(trip.route_avg_km) : '—'}
      </span>
      {isAboveRouteNorm(trip) && (
        <span
          className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-semibold"
          title="Completed more than 5% (and 3 km) above the route average"
        >
          above route norm
        </span>
      )}
    </span>
  );
}

export function FuelUsedCell({ fuel }: { fuel: StatusBoardFuel | null | undefined }) {
  if (!fuel) return dash;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm tabular-nums whitespace-nowrap">
      {fmtL(fuel.consumed_l)}
      <ProvenanceMarker provenance={fuel.provenance} />
      <span className="text-slate-500">{fmtPct(fuel.pct_budget)}</span>
      <OverConsumptionTag fuel={fuel} />
    </span>
  );
}

export function BudgetCell({ fuel }: { fuel: StatusBoardFuel | null | undefined }) {
  if (!fuel) return dash;
  const adjusted = fuel.budget_adjusted_l;
  const original = fuel.budget_original_l;
  if (adjusted != null && original != null && Number(adjusted) !== Number(original)) {
    return (
      <span
        className="text-sm tabular-nums whitespace-nowrap"
        title="Original allowance → adjusted for approved deviations / re-plans"
      >
        <span className="text-slate-400 line-through mr-1">{fmtNum(original)}</span>
        {fmtL(adjusted)}
      </span>
    );
  }
  return <span className="text-sm tabular-nums">{fmtL(adjusted ?? original)}</span>;
}

export function EtaCell({ trip }: { trip: StatusBoardTrip | null | undefined }) {
  if (!trip?.eta) return dash;
  const delta = trip.eta_delta_min;
  return (
    <span className="text-sm tabular-nums whitespace-nowrap">
      {fmtTime(trip.eta)}
      {delta != null && Number(delta) !== 0 && (
        <span className={`ml-1 text-xs ${Number(delta) > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
          ({Number(delta) > 0 ? '+' : '−'}
          {Math.abs(Math.round(Number(delta)))}m)
        </span>
      )}
    </span>
  );
}

/** Where the truck is: the place name, else its coordinates (the Location column; the phone cards). */
export const locationText = (row: StatusBoardRow): string | null => {
  const p = row.position;
  if (!p) return null;
  if (p.place) return p.place;
  if (p.lat != null && p.lon != null)
    return `${Number(p.lat).toFixed(4)}, ${Number(p.lon).toFixed(4)}`;
  return null;
};

export const COLUMNS: StatusBoardColumn[] = [
  {
    id: 'asset',
    label: 'Asset',
    locked: true,
    sortValue: (r) => rowRegistration(r),
    render: (r) => (
      <span className="whitespace-nowrap">
        <span className="text-sm font-mono font-medium text-slate-900">{rowRegistration(r)}</span>
        {r.fleet_no && (
          <span className="ml-1.5 text-xs text-slate-500 font-mono">{r.fleet_no}</span>
        )}
      </span>
    ),
    csv: (r) => rowRegistration(r),
  },
  {
    id: 'driver',
    label: 'Driver',
    sortValue: (r) => rowDriver(r) || '',
    render: (r) =>
      rowDriver(r) ? (
        <span className="text-sm text-slate-700 whitespace-nowrap">{rowDriver(r)}</span>
      ) : (
        dash
      ),
    csv: (r) => rowDriver(r) || '',
  },
  {
    id: 'status',
    label: 'Status',
    sortValue: (r) => combinedStatusLabel(r.status),
    // The truck's key from its whole row: the same colour as on the control room and the maps.
    render: (r, ctx) => <StatusPill status={r.status} statusKey={statusKeyOfRow(r, ctx.now ?? Date.now())} display={r.display} />,
    csv: (r) => combinedStatusLabel(r.status),
  },
  {
    id: 'location',
    label: 'Location',
    sortValue: (r) => locationText(r) || '',
    render: (r) => {
      const t = locationText(r);
      return t ? <span className="text-sm text-slate-700 whitespace-nowrap">{t}</span> : dash;
    },
    csv: (r) => locationText(r) || '',
  },
  {
    id: 'speed',
    label: 'Speed',
    sortValue: (r) => Number(r.position?.speed_kmh ?? -1),
    render: (r) =>
      r.position?.speed_kmh != null ? (
        <span className="text-sm tabular-nums whitespace-nowrap">
          {Math.round(r.position.speed_kmh)} km/h
          {r.position.speed_limit_kmh != null && (
            <span className="text-xs text-slate-500"> ({r.position.speed_limit_kmh})</span>
          )}
        </span>
      ) : (
        dash
      ),
    csv: (r) => r.position?.speed_kmh ?? '',
  },
  {
    id: 'trip',
    label: 'Trip & route',
    sortValue: (r) => r.trip?.route?.name || r.trip?.reference || '',
    render: (r) =>
      r.trip ? (
        <span className="block whitespace-nowrap">
          <span className="text-sm text-slate-900">
            {r.trip.from_name} → {r.trip.to_name}
          </span>
          <span className="block text-xs text-slate-500 font-mono">
            {r.trip.reference}
            {r.trip.route?.version != null &&
              ` · ${r.trip.route.code || 'route'} v${r.trip.route.version}`}
            {r.trip.route?.status === 'proposed' && ' · proposed route'}
          </span>
        </span>
      ) : r.next_trip ? (
        <span className="block whitespace-nowrap text-sm text-slate-600">
          Next: {r.next_trip.from_name} → {r.next_trip.to_name}
          <span className="block text-xs text-slate-500 font-mono">
            {r.next_trip.reference} · planned
          </span>
        </span>
      ) : (
        <span className="text-xs text-slate-400 italic">No active trip</span>
      ),
    csv: (r) => (r.trip ? `${r.trip.reference} ${r.trip.from_name} → ${r.trip.to_name}` : ''),
  },
  {
    id: 'stage',
    label: 'Stage',
    sortValue: (r) => stageLabel(r.trip?.stage),
    render: (r) => (
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        <StagePill stage={r.trip?.stage} />
        <AckRemindersTag trip={r.trip} />
        <AwaitingReportTag trip={r.trip} />
      </span>
    ),
    csv: (r) => stageLabel(r.trip?.stage),
  },
  {
    id: 'distance',
    label: 'Distance on route',
    sortValue: (r) => Number(r.trip?.pct_distance ?? -1),
    render: (r) => <DistanceCell trip={r.trip} />,
    csv: (r) => r.trip?.km_travelled ?? '',
  },
  {
    id: 'remaining',
    label: 'Remaining',
    sortValue: (r) => Number(r.trip?.km_remaining ?? Infinity),
    render: (r) =>
      r.trip?.km_remaining != null ? (
        <span className="text-sm tabular-nums whitespace-nowrap">
          {fmtNum(r.trip.km_remaining)} km
        </span>
      ) : (
        dash
      ),
    csv: (r) => r.trip?.km_remaining ?? '',
  },
  {
    id: 'eta',
    label: 'ETA',
    sortValue: (r) => (r.trip?.eta ? new Date(r.trip.eta).getTime() : Infinity),
    render: (r) => <EtaCell trip={r.trip} />,
    csv: (r) => r.trip?.eta || '',
  },
  {
    id: 'budget',
    label: 'Budget',
    sortValue: (r) => Number(r.fuel?.budget_adjusted_l ?? r.fuel?.budget_original_l ?? -1),
    render: (r) => <BudgetCell fuel={r.fuel} />,
    csv: (r) => r.fuel?.budget_adjusted_l ?? r.fuel?.budget_original_l ?? '',
  },
  {
    id: 'used',
    label: 'Used',
    sortValue: (r) => Number(r.fuel?.pct_budget ?? -1),
    render: (r) => <FuelUsedCell fuel={r.fuel} />,
    csv: (r) => r.fuel?.consumed_l ?? '',
  },
  {
    id: 'burn',
    label: 'Burn',
    sortValue: (r) => Number(r.fuel?.burn_index ?? -1),
    render: (r) =>
      r.fuel ? <BurnIndex value={r.fuel.burn_index} pctDistance={r.trip?.pct_distance} /> : dash,
    csv: (r) => r.fuel?.burn_index ?? '',
  },
  {
    id: 'overunder',
    label: 'Over / under',
    sortValue: (r) => Number(r.fuel?.over_under_l ?? 0),
    render: (r) =>
      r.fuel?.over_under_l != null ? (
        <span
          className="text-sm tabular-nums whitespace-nowrap text-slate-700"
          title="Projected at completion against the adjusted allowance (negative = over)"
        >
          {fmtSignedL(r.fuel.over_under_l)}
        </span>
      ) : (
        dash
      ),
    csv: (r) => r.fuel?.over_under_l ?? '',
  },
  {
    id: 'overunder_r',
    label: 'Over / under (R)',
    manager: true,
    defaultVisible: false,
    sortValue: (r) => Number(r.fuel?.over_under_r ?? 0),
    render: (r) =>
      r.fuel?.over_under_r != null ? (
        <span className="text-sm tabular-nums whitespace-nowrap">
          {fmtRand(r.fuel.over_under_r)}
        </span>
      ) : (
        dash
      ),
    csv: (r) => r.fuel?.over_under_r ?? '',
  },
  {
    id: 'run',
    label: 'Run',
    sortValue: (r) => Number(r.run?.trip_seq ?? -1),
    render: (r, ctx) => <RunChip run={r.run} onOpen={ctx.onOpenRun} />,
    csv: (r) => (r.run ? `${r.run.trip_seq}/${r.run.trip_count}` : ''),
  },
  {
    id: 'inStatus',
    label: 'In status',
    sortValue: (r, ctx) => secondsSince(statusSince(r), ctx.now) ?? -1,
    render: (r, ctx) => (
      <span className="text-sm tabular-nums whitespace-nowrap text-slate-700">
        {fmtDuration(secondsSince(statusSince(r), ctx.now))}
      </span>
    ),
    csv: (r, ctx) => fmtDuration(secondsSince(statusSince(r), ctx.now)),
  },
  {
    id: 'updated',
    label: 'Updated',
    locked: true,
    sortValue: (r, ctx) => secondsSince(rowUpdatedAt(r), ctx.now) ?? Infinity,
    render: (r, ctx) => <DataAge ts={rowUpdatedAt(r)} now={ctx.now} />,
    csv: (r) => rowUpdatedAt(r) || '',
  },
];

export const DEFAULT_VISIBLE = COLUMNS.filter((c) => c.defaultVisible !== false).map((c) => c.id);
